import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Loader2, Check } from 'lucide-react';

/**
 * Iter200 — Footer newsletter signup strip.
 *
 * Compact inline form embedded in the dark site Footer. Renders 3 inputs
 * (Όνομα, Επώνυμο, Email), a GDPR consent checkbox, and an "Εγγραφή" CTA.
 * Posts to `POST /api/newsletter/subscribe` and shows a success card when
 * the double-opt-in email has been queued.
 *
 * Behaviour:
 *   - Disables the CTA until consent is ticked AND all 3 fields are filled.
 *   - Locale (gr|en) is sent along so the confirmation email matches the
 *     user's current site language.
 *   - On 429 (rate-limited) shows a friendly "δοκίμασε ξανά σε λίγο"
 *     message — does NOT leak how many attempts remain.
 *   - On success, swaps the form for a green check + "Έλεγξε το email
 *     σου" line that mirrors what the user is being told to do next.
 */
const NewsletterFooterStrip = () => {
  const { i18n } = useTranslation();
  const isGr = i18n.language === 'gr';
  const t = (en, gr) => (isGr ? gr : en);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const canSubmit = consent && firstName.trim() && lastName.trim() && email.trim() && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          consent: true,
          locale: isGr ? 'gr' : 'en',
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const msg = data?.detail?.message
          || (r.status === 429
            ? t('Too many attempts — try again in a bit.',
                'Πολλές προσπάθειες — δοκίμασε ξανά σε λίγο.')
            : t('Something went wrong, please try again.',
                'Κάτι πήγε στραβά, παρακαλώ προσπάθησε ξανά.'));
        setError(msg);
        return;
      }
      setSuccess(true);
    } catch (err) {
      setError(t('Network error, please try again.',
                 'Σφάλμα δικτύου, παρακαλώ προσπάθησε ξανά.'));
    } finally {
      setBusy(false);
    }
  };

  if (success) {
    return (
      <div
        className="bg-gray-800/60 border border-emerald-700/40 rounded-lg p-5 flex items-start gap-3"
        data-testid="newsletter-success-card"
      >
        <Check className="w-6 h-6 text-emerald-400 flex-shrink-0 mt-0.5" />
        <div>
          <div className="text-emerald-300 font-semibold mb-1">
            {t('Almost there!', 'Ένα ακόμη βήμα!')}
          </div>
          <div className="text-gray-300 text-sm leading-relaxed">
            {t(
              'Check your inbox to confirm the subscription. The link expires in 7 days.',
              'Έλεγξε τα εισερχόμενά σου για να επιβεβαιώσεις την εγγραφή. Ο σύνδεσμος λήγει σε 7 ημέρες.'
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="newsletter-footer-form">
      <div className="flex items-center gap-2 text-sm text-gray-300 mb-2">
        <Mail className="w-4 h-4" />
        <span>
          {t('Get expedition news, new courses, and trail events.',
             'Νέα από εξορμήσεις, σχολές και δράσεις του συλλόγου.')}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input
          type="text"
          required
          autoComplete="given-name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder={t('First name', 'Όνομα')}
          className="bg-gray-800 border border-gray-700 text-white placeholder-gray-500 rounded px-3 py-2 text-sm focus:border-orange-500 focus:outline-none"
          data-testid="newsletter-first-name"
        />
        <input
          type="text"
          required
          autoComplete="family-name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder={t('Last name', 'Επώνυμο')}
          className="bg-gray-800 border border-gray-700 text-white placeholder-gray-500 rounded px-3 py-2 text-sm focus:border-orange-500 focus:outline-none"
          data-testid="newsletter-last-name"
        />
      </div>
      <input
        type="email"
        required
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t('Your email', 'Το email σου')}
        className="w-full bg-gray-800 border border-gray-700 text-white placeholder-gray-500 rounded px-3 py-2 text-sm focus:border-orange-500 focus:outline-none"
        data-testid="newsletter-email"
      />
      <label className="flex items-start gap-2 text-xs text-gray-400 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 accent-orange-500"
          data-testid="newsletter-consent"
        />
        <span>
          {t(
            'I agree to receive newsletters from E.O.S. Ilioupoli. You can unsubscribe at any time.',
            'Συμφωνώ να λαμβάνω newsletters από τον Ε.Ο.Σ. Ηλιούπολης. Μπορείς να διαγραφείς όποτε θέλεις.'
          )}
        </span>
      </label>
      {error && (
        <div
          className="text-red-400 text-xs bg-red-950/30 border border-red-900/50 rounded px-3 py-2"
          data-testid="newsletter-error"
        >
          {error}
        </div>
      )}
      <button
        type="submit"
        disabled={!canSubmit}
        className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed text-white font-semibold py-2.5 rounded transition-colors flex items-center justify-center gap-2"
        data-testid="newsletter-submit"
      >
        {busy ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            {t('Subscribing…', 'Εγγραφή…')}
          </>
        ) : (
          t('Subscribe', 'Εγγραφή')
        )}
      </button>
    </form>
  );
};

export default NewsletterFooterStrip;
